import _ from 'lodash'
import {FileRejection} from 'react-dropzone'

import {supportedRenderingFormats} from '@/components/application-draft/utils/constants'
import {useFileUploadContent} from '@/content/fileUpload'
import {DocumentType} from '@/utils/document/constants'

import {Document_Bool_Exp, DocumentFragment, Order_By} from '../graphqlSdk'
import {brandSubscriptionOrm} from '../orm-tools/brandSubscription'
import {documentOrm} from '../orm-tools/document'
import {productOrm} from '../orm-tools/product'
import {OrderDocument} from '../user-portal/types'

export const getFileExtension = (fileName: string) =>
  (_.last(fileName.split('.')) ?? '').trim().toLowerCase()

export const replaceFileExtension = (fileName: string, newExtension: string): string => {
  return `${fileName.substring(0, fileName.lastIndexOf('.')) || fileName}${newExtension}`
}

export const isSupportedExtension = (fileName: string) => {
  const extension = getFileExtension(fileName)
  return supportedRenderingFormats.includes(extension)
}

export const getFileImageSrc = (blob: Blob, fileName: string) => {
  const extension = getFileExtension(fileName)

  if (['png', 'jpeg', 'jpg', 'bmp', 'gif'].includes(extension)) return URL.createObjectURL(blob)
  if ('svg' === extension) {
    const svgBlob = blob.slice(0, blob.size, 'image/svg+xml')
    return URL.createObjectURL(svgBlob)
  }
}

export const getDocumentFileName = (path: string) => {
  // name consists datetime, strip it
  const pathName = _.last(path.split('/')) ?? ''
  return pathName.substring(pathName.indexOf('-') + 1)
}

export const isImageDocument = (path: string) => {
  const ext = getFileExtension(path)
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
}

export const getTruncatedFileName = (fileName: string) => {
  const length = fileName.length

  const truncatedFileName =
    fileName.length > 55
      ? fileName.substring(0, 35) + ' ... ' + fileName.substring(length - 15)
      : fileName

  return truncatedFileName
}

export const useRejectedFileErrorMessage = (
  fileRejections: FileRejection[],
  maxFileSize: number,
  maxFiles?: number,
) => {
  const {
    tooManyFilesErrorMessage,
    moreThanOneFileErrorMessage,
    formatErrorMessage,
    sizeErrorMessage,
  } = useFileUploadContent()

  return fileRejections.length > 0
    ? fileRejections[0].errors[0].code === 'too-many-files'
      ? maxFiles === 1
        ? moreThanOneFileErrorMessage
        : tooManyFilesErrorMessage
      : fileRejections[0].file.size > maxFileSize
      ? sizeErrorMessage
      : formatErrorMessage
    : ''
}

export const findMostRecentDocument = (
  documents: DocumentFragment[],
  type: DocumentType,
  documentFolderId?: string,
): DocumentFragment | undefined =>
  _.maxBy(
    documents.filter(
      (document) =>
        document.type === type &&
        (!documentFolderId || document.document_folder_id === documentFolderId),
    ),
    'created_at',
  )

export const getLastDocumentByRevision = <T extends OrderDocument>(
  documents: T[],
  type: DocumentType,
): T | undefined =>
  _.last(
    _.sortBy(
      documents.filter((document) => document.type === type),
      (document) => document.revision,
    ),
  )

export const getMaxFileSizeInMB = (maxFileSize: number) => maxFileSize / (1024 * 1024)

export const formatFileSize = (size: number) => {
  const sufixes = ['B', 'kB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(size) / Math.log(1024))
  return `${(size / Math.pow(1024, i)).toFixed(1)} ${sufixes[i]}`
}

export const getDocumentByProductId = async (
  id: string,
  documentType: DocumentType,
  userId?: string,
): Promise<DocumentFragment | null> => {
  const product = await productOrm.getById(id)

  if (
    !product.registration_request &&
    !product.service_request &&
    !product.brand_subscription_payment
  ) {
    return null
  }

  const filter: Document_Bool_Exp = {
    type: {_eq: documentType},
    ...(userId ? {document_hub: {user_id: {_eq: userId}}} : {}),
  }
  if (product?.registration_request) {
    _.assign(filter, {document_hub_id: {_eq: product.registration_request.document_hub_id}})
  }

  if (product?.service_request) {
    _.assign(filter, {document_hub_id: {_eq: product.service_request.document_hub_id}})
  }

  if (product.brand_subscription_payment) {
    const brandSubscription = await brandSubscriptionOrm.getById(
      product.brand_subscription_payment.brand_subscription_id,
    )
    _.assign(filter, {
      document_hub_id: {_eq: brandSubscription.document_hub_id},
      document_folder_id: {_eq: product.brand_subscription_payment.document_folder_id},
    })
  }

  const documents = await documentOrm.getMany(filter, {created_at: Order_By.DescNullsLast})

  return documents[0] ?? null
}
